import { Button } from '#app/components/ui/button.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { cn } from '#app/utils/misc.tsx'
import { type JournalLine, type Kind } from './ledger.ts'

interface JournalEditorProps {
	lines: JournalLine[]
	onChange: (next: JournalLine[]) => void
	maxLines?: number
	/** Lock the grid (e.g. an already-committed immutable version). */
	disabled?: boolean
}

export function JournalEditor({
	lines,
	onChange,
	maxLines = 10,
	disabled = false,
}: JournalEditorProps) {
	function update(id: string, patch: Partial<JournalLine>) {
		onChange(lines.map((l) => (l.id === id ? { ...l, ...patch } : l)))
	}
	function remove(id: string) {
		onChange(lines.filter((l) => l.id !== id))
	}
	function add() {
		if (lines.length >= maxLines) return
		const last = lines[lines.length - 1]
		onChange([
			...lines,
			{
				id: crypto.randomUUID(),
				date: last?.date ?? '2026-01-01',
				kind: 'revenue',
				amount: 0,
			},
		])
	}

	return (
		<div className="space-y-2">
			{lines.map((line) => (
				<div
					key={line.id}
					className="border-border/60 space-y-2 rounded-md border p-2"
				>
					<div className="flex items-center gap-2">
						<Input
							type="date"
							value={line.date}
							disabled={disabled}
							onChange={(e) => update(line.id, { date: e.target.value })}
							className="h-8 flex-1"
						/>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={disabled}
							onClick={() => remove(line.id)}
							className="text-muted-foreground h-8 w-8 shrink-0 p-0 hover:text-red-500"
							aria-label="Remove line"
						>
							×
						</Button>
					</div>
					<KindToggle
						value={line.kind}
						disabled={disabled}
						onChange={(kind) => update(line.id, { kind })}
					/>
					<Input
						type="number"
						min={0}
						step={50}
						aria-label="Amount"
						placeholder="Amount"
						value={Number.isFinite(line.amount) ? line.amount : 0}
						disabled={disabled}
						onChange={(e) =>
							update(line.id, { amount: Number(e.target.value) })
						}
						className="h-8 w-full text-right"
					/>
				</div>
			))}
			{!disabled && (
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={add}
					disabled={lines.length >= maxLines}
					className="mt-1 w-full"
				>
					+ Add line{' '}
					<span className="text-muted-foreground ml-1">
						({lines.length}/{maxLines})
					</span>
				</Button>
			)}
		</div>
	)
}

function KindToggle({
	value,
	onChange,
	disabled,
}: {
	value: Kind
	onChange: (k: Kind) => void
	disabled?: boolean
}) {
	return (
		<div className="flex w-full overflow-hidden rounded-md border">
			{(['revenue', 'expense'] as const).map((k) => (
				<button
					key={k}
					type="button"
					disabled={disabled}
					onClick={() => onChange(k)}
					className={cn(
						'h-8 flex-1 text-xs font-medium capitalize transition-colors disabled:opacity-60',
						value === k
							? k === 'revenue'
								? 'bg-emerald-500 text-white'
								: 'bg-rose-500 text-white'
							: 'bg-background text-muted-foreground hover:bg-muted',
					)}
				>
					{k}
				</button>
			))}
		</div>
	)
}
